import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Clock, CheckCircle, ArrowRight, TrendingUp, Award, Target } from 'lucide-react';
import { designSystem } from '../designSystem';

// Helper function to clean up content that has mixed HTML and markdown
const cleanContent = (content) => {
  if (!content) return '';
  // Remove markdown ** bold markers (since HTML bold tags are already present)
  let cleaned = content.replace(/\*\*([^*]+)\*\*/g, '$1');
  // Also remove any standalone ** markers
  cleaned = cleaned.replace(/\*\*/g, '');
  return cleaned;
};

const LearningModulesPage = () => {
  const [pillars, setPillars] = useState([]);
  const [selectedPillar, setSelectedPillar] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAssessment, setShowAssessment] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [assessmentResults, setAssessmentResults] = useState([]);
  
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const userId = userData.user_id || userData.id || 'demo_user';
  const organizationId = userData.organization_id || 'demo_org';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch pillars and user progress in parallel
      const [pillarsRes, progressRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/pillars/all`),
        fetch(`${BACKEND_URL}/api/pillars/progress/${userId}`)
      ]);
      
      const pillarsData = await pillarsRes.json();
      const progressData = await progressRes.json();
      
      setPillars(pillarsData);
      setUserProgress(progressData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const getModuleProgress = (pillarId, moduleId) => {
    if (!userProgress?.pillars_progress) return null;
    const pillar = userProgress.pillars_progress.find(p => p.pillar_id === pillarId);
    if (!pillar) return null;
    return pillar.modules_progress?.find(m => m.sub_pillar_id === moduleId);
  };

  const getPillarProgress = (pillarId) => {
    if (!userProgress?.pillars_progress) return null;
    return userProgress.pillars_progress.find(p => p.pillar_id === pillarId);
  };

  const handlePillarSelect = async (pillar) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pillars/${pillar.pillar_id}`);
      const fullPillar = await response.json();
      setSelectedPillar(fullPillar);
    } catch (error) {
      console.error('Error fetching pillar details:', error);
    }
  };

  const handleModuleSelect = async (module) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/pillars/${selectedPillar.pillar_id}/module/${module.sub_pillar_id}`
      );
      const moduleData = await response.json();
      setSelectedModule(moduleData);
      
      // Mark module as started
      await fetch(`${BACKEND_URL}/api/pillars/progress/update?user_id=${userId}&organization_id=${organizationId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pillar_id: selectedPillar.pillar_id,
          sub_pillar_id: module.sub_pillar_id,
          action: 'start_module'
        })
      });
    } catch (error) {
      console.error('Error fetching module:', error);
    }
  };

  const completeModule = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/pillars/progress/update?user_id=${userId}&organization_id=${organizationId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pillar_id: selectedPillar.pillar_id,
          sub_pillar_id: selectedModule.sub_pillar_id,
          action: 'complete_module',
          time_spent_minutes: 15
        })
      });
      
      // Refresh progress
      await fetchData();
      setSelectedModule(null);
      setSelectedPillar(null);
    } catch (error) {
      console.error('Error completing module:', error);
    }
  };

  const startAssessment = () => {
    if (selectedModule?.assessment_questions?.length > 0) {
      setShowAssessment(true);
      setCurrentQuestion(0);
      setSelectedAnswer(null);
      setAssessmentResults([]);
    }
  };

  const submitAnswer = async () => {
    if (selectedAnswer === null) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/pillars/assessment/submit?user_id=${userId}&organization_id=${organizationId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pillar_id: selectedPillar.pillar_id,
          sub_pillar_id: selectedModule.sub_pillar_id,
          question_index: currentQuestion,
          selected_answer: selectedAnswer
        })
      });
      
      const result = await response.json();
      setAssessmentResults([...assessmentResults, result]);

      if (currentQuestion < selectedModule.assessment_questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer(null);
      } else {
        // Assessment complete
        setTimeout(() => {
          setShowAssessment(false);
          completeModule();
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading Learning Modules...</p>
        </div>
      </div>
    );
  }

  // Pillars Overview
  if (!selectedPillar) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-extrabold mb-3 tracking-tight" style={{ fontFamily: designSystem.typography.fonts.display, fontSize: designSystem.typography.sizes['4xl'] }}>6 Pillars Learning Journey</h1>
                <p className="text-blue-50 text-lg font-medium" style={{ fontSize: designSystem.typography.sizes.lg }}>Master conscious business excellence through structured learning</p>
              </div>
              <button
                onClick={() => navigate('/my-progress')}
                className="px-8 py-4 bg-white text-indigo-600 rounded-2xl hover:bg-gray-50 transition-all shadow-2xl hover:shadow-3xl flex items-center space-x-3 font-bold transform hover:scale-105"
                style={{ fontSize: designSystem.typography.sizes.lg }}
              >
                <TrendingUp className="h-6 w-6" />
                <span>View Detailed Progress</span>
              </button>
            </div>
          </div>
        </div>

        {/* Progress Summary */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
            <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-green-100 transform hover:scale-105">
              <div className="flex items-center space-x-6">
                <div className="p-4 bg-green-500 rounded-2xl shadow-lg">
                  <CheckCircle className="h-10 w-10 text-white" />
                </div>
                <div>
                  <p className="text-base font-bold text-gray-700 uppercase tracking-wide" style={{ fontSize: designSystem.typography.sizes.sm }}>Pillars Completed</p>
                  <p className="text-4xl font-extrabold text-gray-900 mt-1" style={{ fontFamily: designSystem.typography.fonts.display }}>{userProgress?.total_pillars_completed || 0}/8</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-blue-100 transform hover:scale-105">
              <div className="flex items-center space-x-6">
                <div className="p-4 bg-blue-500 rounded-2xl shadow-lg">
                  <Target className="h-10 w-10 text-white" />
                </div>
                <div>
                  <p className="text-base font-bold text-gray-700 uppercase tracking-wide" style={{ fontSize: designSystem.typography.sizes.sm }}>Overall Progress</p>
                  <p className="text-4xl font-extrabold text-gray-900 mt-1" style={{ fontFamily: designSystem.typography.fonts.display }}>{Math.round(userProgress?.overall_completion_percentage || 0)}%</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-purple-100 transform hover:scale-105">
              <div className="flex items-center space-x-6">
                <div className="p-4 bg-purple-500 rounded-2xl shadow-lg">
                  <Award className="h-10 w-10 text-white" />
                </div>
                <div>
                  <p className="text-base font-bold text-gray-700 uppercase tracking-wide" style={{ fontSize: designSystem.typography.sizes.sm }}>Learning Path</p>
                  <p className="text-2xl font-extrabold text-gray-900 mt-1" style={{ fontFamily: designSystem.typography.fonts.display }}>Conscious Excellence</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pillars Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {pillars.map((pillar) => {
              const pillarProg = getPillarProgress(pillar.pillar_id);
              const completedModules = pillarProg?.modules_progress?.filter(m => m.completed_at).length || 0;
              const totalModules = pillar.sub_pillars?.length || 0;
              const progressPercent = totalModules > 0 ? (completedModules / totalModules) * 100 : 0;

              return (
                <div
                  key={pillar.pillar_id}
                  onClick={() => handlePillarSelect(pillar)}
                  className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-gray-200 hover:border-indigo-300 group transform hover:scale-102"
                >
                  <div className="flex items-start space-x-6">
                    <div className="text-6xl group-hover:scale-110 transition-transform duration-300">
                      {pillar.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-extrabold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors" style={{ fontFamily: designSystem.typography.fonts.display, fontSize: designSystem.typography.sizes['2xl'] }}>
                        Pillar {pillar.pillar_number}: {pillar.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4" dangerouslySetInnerHTML={{ __html: cleanContent(pillar.description) }} />
                      
                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-gray-600">Progress</span>
                          <span className="font-semibold text-gray-900">{Math.round(progressPercent)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 h-2 rounded-full transition-all"
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-4 text-gray-500">
                          <span className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {pillar.total_duration_minutes} min
                          </span>
                          <span className="flex items-center">
                            <BookOpen className="h-4 w-4 mr-1" />
                            {completedModules}/{totalModules} modules
                          </span>
                        </div>
                        {pillarProg?.completed_at && (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Modules View
  if (selectedPillar && !selectedModule) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => setSelectedPillar(null)}
            className="mb-6 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
          >
            <ArrowRight className="h-4 w-4 rotate-180" />
            <span>Back to Pillars</span>
          </button>

          {/* Pillar Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-8 mb-8 shadow-xl text-white">
            <div className="flex items-start space-x-6">
              <div className="text-7xl">{selectedPillar.icon}</div>
              <div className="flex-1">
                <h2 className="text-4xl font-bold mb-4">
                  Pillar {selectedPillar.pillar_number}: {selectedPillar.name}
                </h2>
                <p className="text-blue-100 text-lg mb-6" dangerouslySetInnerHTML={{ __html: cleanContent(selectedPillar.description) }} />
                <div className="flex items-center space-x-8 text-sm">
                  <span className="flex items-center">
                    <Clock className="h-5 w-5 mr-2" />
                    {selectedPillar.total_duration_minutes} minutes total
                  </span>
                  <span className="flex items-center">
                    <BookOpen className="h-5 w-5 mr-2" />
                    {selectedPillar.sub_pillars?.length || 0} learning modules
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Modules List */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Learning Modules</h3>
            {selectedPillar.sub_pillars?.map((module, index) => {
              const moduleProg = getModuleProgress(selectedPillar.pillar_id, module.sub_pillar_id);
              const isCompleted = moduleProg?.completed_at;

              return (
                <div key={module.sub_pillar_id} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all border border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className="bg-blue-100 text-blue-800 text-sm font-bold px-4 py-2 rounded-full">
                          Module {index + 1}
                        </span>
                        <span className="text-gray-500 text-sm flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {module.duration_minutes} min
                        </span>
                        {isCompleted && (
                          <span className="bg-green-100 text-green-800 text-sm font-bold px-3 py-1 rounded-full flex items-center">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Completed
                          </span>
                        )}
                      </div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2" dangerouslySetInnerHTML={{ __html: cleanContent(module.name) }} />
                      <p className="text-gray-600" dangerouslySetInnerHTML={{ __html: cleanContent(module.description) }} />
                    </div>
                    <button
                      onClick={() => handleModuleSelect(module)}
                      className="ml-6 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg flex items-center space-x-2"
                    >
                      <span>{isCompleted ? 'Review' : 'Start Learning'}</span>
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Additional Resources */}
          {selectedPillar.additional_resources?.length > 0 && (
            <div className="mt-8 bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Additional Resources</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {selectedPillar.additional_resources.map((resource) => (
                  <a
                    key={resource.resource_id}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-all"
                  >
                    <h5 className="font-semibold text-gray-900 mb-2">{resource.title}</h5>
                    <p className="text-sm text-gray-600">{resource.description}</p>
                    <p className="text-xs text-blue-600 mt-2 uppercase font-semibold">{resource.type}</p>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Module Learning View
  if (selectedModule) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => setSelectedModule(null)}
            className="mb-6 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
          >
            <ArrowRight className="h-4 w-4 rotate-180" />
            <span>Back to Modules</span>
          </button>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white">
              <h2 className="text-3xl font-bold mb-2" dangerouslySetInnerHTML={{ __html: cleanContent(selectedModule.name) }} />
              <p className="text-blue-100" dangerouslySetInnerHTML={{ __html: cleanContent(selectedModule.description) }} />
            </div>

            <div className="p-8">
              <div
                className="prose prose-lg max-w-none mb-8 
                           prose-headings:text-gray-900 prose-headings:font-bold
                           prose-h2:text-2xl prose-h2:mb-4 prose-h2:mt-8
                           prose-h3:text-xl prose-h3:mb-3 prose-h3:mt-6
                           prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                           prose-ul:list-disc prose-ul:ml-6 prose-ul:mb-4
                           prose-li:text-gray-700 prose-li:mb-2
                           prose-strong:text-gray-900 prose-strong:font-semibold
                           prose-em:text-gray-600 prose-em:italic"
                dangerouslySetInnerHTML={{ __html: cleanContent(selectedModule.content) }}
              />

              {/* Assessment Section */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Test Your Understanding</h4>
                    <p className="text-gray-600">
                      Quick knowledge check with {selectedModule.assessment_questions?.length || 0} questions (Optional)
                    </p>
                  </div>
                  <div className="flex space-x-4">
                    <button
                      onClick={completeModule}
                      className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Skip Assessment
                    </button>
                    <button
                      onClick={startAssessment}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg flex items-center space-x-2"
                    >
                      <span>Take Assessment</span>
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Assessment Modal */}
          {showAssessment && selectedModule && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                <div className="p-6 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                  <h3 className="text-2xl font-bold">Quick Knowledge Check</h3>
                  <p className="text-blue-100 mt-2">
                    Question {currentQuestion + 1} of {selectedModule.assessment_questions.length}
                  </p>
                </div>

                <div className="p-8">
                  <p className="text-xl font-medium text-gray-900 mb-6">
                    {selectedModule.assessment_questions[currentQuestion].question}
                  </p>

                  <div className="space-y-3 mb-8">
                    {selectedModule.assessment_questions[currentQuestion].options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedAnswer(index)}
                        className={`w-full text-left p-5 rounded-lg border-2 transition-all ${
                          selectedAnswer === index
                            ? 'border-blue-600 bg-blue-50 shadow-md'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center space-x-4">
                          <span className="font-bold text-lg text-gray-700 w-8">
                            {String.fromCharCode(65 + index)}.
                          </span>
                          <span className="text-gray-900">{option}</span>
                        </div>
                      </button>
                    ))}
                  </div>

                  <div className="flex justify-between items-center pt-6 border-t">
                    <button
                      onClick={completeModule}
                      className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Skip Assessment
                    </button>
                    <button
                      onClick={submitAnswer}
                      disabled={selectedAnswer === null}
                      className={`px-8 py-3 rounded-lg flex items-center space-x-2 transition-all ${
                        selectedAnswer !== null
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <span>
                        {currentQuestion === selectedModule.assessment_questions.length - 1
                          ? 'Complete'
                          : 'Next Question'}
                      </span>
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>

                  {assessmentResults.length === selectedModule.assessment_questions.length && (
                    <div className="mt-6 p-6 bg-green-50 border-2 border-green-200 rounded-xl">
                      <div className="flex items-center space-x-4">
                        <CheckCircle className="h-8 w-8 text-green-600" />
                        <div>
                          <h4 className="text-xl font-bold text-green-900">Assessment Complete!</h4>
                          <p className="text-green-700 mt-1">
                            You scored {assessmentResults.filter(r => r.is_correct).length} out of{' '}
                            {selectedModule.assessment_questions.length}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default LearningModulesPage;
